import { useState } from 'react';

import './VideoEmbed.scss';

import { track } from '../utility/analytics';

// A click-to-load YouTube facade: a poster frame and a play button, with the
// real iframe mounted only once someone asks for it.
//
// The reason is weight. A YouTube embed pulls roughly a megabyte of player
// JavaScript plus a run of requests to three hosts, and it does that on page
// load whether or not the visitor ever presses play. With more than one film on
// the page that is the single largest thing the site downloads, and most of it
// is spent on people who scroll straight past. The poster below is ~30KB.
//
// youtube-nocookie.com rather than youtube.com: no cookie is set until playback
// actually starts, which keeps the site in the same no-consent-banner position
// the cookieless analytics choice already put it in.
//
// `name` is the label this film reports under in Umami — keep it stable, a
// rename splits its own history. It is a title, never anything a visitor typed.
function VideoEmbed({ youtubeID, poster, title, name, label = 'Play' }) {
    const [isPlaying, setIsPlaying] = useState(false);

    // autoplay=1 because the visitor already clicked once: without it the
    // facade costs them a second click on the real player for no reason.
    // rel=0 keeps the end-card suggestions inside this channel.
    const src = `https://www.youtube-nocookie.com/embed/${youtubeID}?autoplay=1&rel=0`;

    if (isPlaying) {
        return (
            <div className="VideoEmbed">
                <iframe
                    className="VideoEmbed-frame"
                    src={src}
                    title={title}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                />
            </div>
        );
    }

    return (
        <div className="VideoEmbed">
            {/* a real button, not a div with onClick — this has to be reachable
                by keyboard and announced as a control, since it is the only way
                to get to the film at all */}
            <button
                type="button"
                className="VideoEmbed-facade"
                aria-label={`Play video: ${title}`}
                onClick={() => {
                    setIsPlaying(true);
                    track('video-play', { video: name });
                }}>
                <img className="VideoEmbed-poster"
                    src={poster}
                    alt=""
                    aria-hidden="true"
                    loading="lazy"
                    decoding="async" />

                {/* bottom-left rather than centred. The parish poster is a
                    dead-centre composition — the priest elevating the host under
                    the crucifix — and a play triangle stamped over the Eucharist
                    is not something to ship to this audience. Offset works on
                    every poster; centred only works on some. */}
                <span className="VideoEmbed-scrim" aria-hidden="true"></span>

                <span className="VideoEmbed-play" aria-hidden="true">
                    <svg viewBox="0 0 24 24" focusable="false">
                        <path d="M8 5.5v13l11-6.5z" />
                    </svg>
                    <span className="VideoEmbed-play-label">{label}</span>
                </span>
            </button>
        </div>
    );
}

export default VideoEmbed;
